import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "app/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all duration-200 ease-in-out disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Primary button - Sapphire color
        primary:
          "bg-[var(--button-primary-default)] text-[var(--button-primary-text)] hover:bg-[var(--button-primary-hover)] active:bg-[var(--button-primary-pressed)] disabled:bg-[var(--button-primary-disabled)]",

        // Secondary Gray button
        "secondary-gray":
          "bg-[var(--button-secondary-gray-default)] text-[var(--button-secondary-gray-text)] border-[1px] border-[var(--button-secondary-gray-border)] hover:bg-[var(--button-secondary-gray-hover)] active:bg-[var(--button-secondary-gray-pressed)] disabled:text-[var(--button-secondary-gray-disabled-text)] disabled:border-[var(--button-secondary-gray-disabled-border)] focus-visible:bg-[var(--button-secondary-gray-hover)]",

        // Secondary Blue button
        "secondary-blue":
          "bg-[var(--button-secondary-blue-default)] text-[var(--button-secondary-blue-text)] border-[1px] border-[var(--button-secondary-blue-border)] hover:bg-[var(--button-secondary-blue-hover)] active:bg-[var(--button-secondary-blue-pressed)] disabled:text-[var(--button-secondary-blue-disabled-text)] disabled:border-[var(--button-secondary-blue-disabled-border)] focus-visible:bg-[var(--button-secondary-blue-hover)]",

        // Tertiary button (text only)
        tertiary:
          "text-[var(--button-tertiary-text)] hover:bg-[var(--button-tertiary-hover)] active:bg-[var(--button-tertiary-pressed)] disabled:text-[var(--button-tertiary-disabled)]",

        // Tertiary Error button
        "tertiary-error":
          "text-[var(--button-tertiary-error-text)] hover:bg-[var(--button-tertiary-error-hover)] active:bg-[var(--button-tertiary-error-pressed)] disabled:text-[var(--button-tertiary-error-disabled)]",
      },
      size: {
        // Using the measurements from Figma
        sm: "h-6 rounded-[4px] px-2 text-[12px] font-semibold leading-[16px]", // 24px height
        md: "h-[32px] rounded-[4px] px-[11px] py-[6px] text-[14px] font-semibold leading-[20px]", // Using exact px from design
        lg: "h-8 rounded-[4px] px-4 text-[16px] font-bold leading-[24px]", // 32px height
        icon: "h-7 w-7 p-0 rounded-[4px]",
        // New size: Icon with label
        "icon-label":
          "h-[32px] rounded-[4px] px-[11px] py-[6px] gap-[8px] text-[14px] font-semibold leading-[20px]", // Using exact px from design
      },
    },
    defaultVariants: {
      variant: "primary", // Changed from default to primary
      size: "md", // Changed from default to md
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          "button-focus"
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
